import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  ChartLine,
  Users,
  UserPlus,
  TrendUp,
  ArrowRight,
  ArrowsClockwise,
} from '@phosphor-icons/react';

// Simple sparkline component
const Sparkline = ({ data, color = '#3b82f6' }) => {
  if (!data || data.length === 0) return null;
  
  const values = data.map(d => d.dau || 0);
  const max = Math.max(...values, 1);
  const min = Math.min(...values);
  const range = max - min || 1;
  
  const width = 120;
  const height = 32;
  const padding = 2;
  
  const points = values.map((val, i) => {
    const x = padding + (i / (values.length - 1 || 1)) * (width - padding * 2);
    const y = height - padding - ((val - min) / range) * (height - padding * 2);
    return `${x},${y}`;
  }).join(' ');
  
  return (
    <svg width={width} height={height} className="overflow-visible">
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Current value dot */}
      {values.length > 0 && (
        <circle
          cx={width - padding}
          cy={height - padding - ((values[values.length - 1] - min) / range) * (height - padding * 2)}
          r="3"
          fill={color}
        />
      )}
    </svg>
  );
};

export const AdoptionWidget = ({ department = 'substation' }) => {
  const { api, isSupervisorPlus } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!isSupervisorPlus()) {
        setLoading(false);
        return;
      }
      
      try {
        const response = await api.get(`/adoption/${department}/today`);
        setData(response.data);
      } catch (error) {
        console.error('Failed to fetch adoption data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // Refresh every 5 minutes
    const interval = setInterval(fetchData, 300000);
    return () => clearInterval(interval);
  }, [api, department, isSupervisorPlus]);

  if (!isSupervisorPlus()) {
    return null; // Don't show to non-supervisors
  }

  const getTrendIndicator = () => {
    if (!data?.sparkline || data.sparkline.length < 2) return null;
    
    const lastTwo = data.sparkline.slice(-2);
    const trend = lastTwo[1]?.dau - lastTwo[0]?.dau;
    
    if (trend > 0) {
      return <Badge className="bg-emerald-100 text-emerald-700 text-xs">+{trend}</Badge>;
    } else if (trend < 0) {
      return <Badge className="bg-red-100 text-red-700 text-xs">{trend}</Badge>;
    }
    return null;
  };

  return (
    <Card 
      className="border-2 border-indigo-100 bg-gradient-to-br from-indigo-50 to-purple-50 hover:shadow-lg transition-all cursor-pointer group"
      onClick={() => navigate(`/analytics/adoption/${department}`)}
      data-testid="adoption-widget"
    >
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
              <ChartLine size={22} className="text-white" weight="fill" />
            </div>
            <div>
              <CardTitle className="text-lg capitalize">{department} Adoption</CardTitle>
              <CardDescription className="text-xs">Daily user engagement</CardDescription>
            </div>
          </div>
          <ArrowRight size={20} className="text-slate-400 group-hover:text-indigo-600 transition-colors" />
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <ArrowsClockwise size={24} className="animate-spin text-indigo-400" />
          </div>
        ) : data ? (
          <div className="space-y-4">
            {/* Metrics Grid */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white rounded-lg p-3 border border-indigo-100">
                <div className="flex items-center gap-1 mb-1">
                  <Users size={14} className="text-indigo-500" />
                  <span className="text-xs text-slate-500">DAU</span>
                </div>
                <div className="flex items-center gap-2">
                  <p className="text-xl font-bold text-slate-900">{data.dau || 0}</p>
                  {getTrendIndicator()}
                </div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-emerald-100">
                <div className="flex items-center gap-1 mb-1">
                  <UserPlus size={14} className="text-emerald-500" />
                  <span className="text-xs text-slate-500">New Today</span>
                </div>
                <p className="text-xl font-bold text-slate-900">{data.new_users || 0}</p>
              </div>
              <div className="bg-white rounded-lg p-3 border border-purple-100">
                <div className="flex items-center gap-1 mb-1">
                  <TrendUp size={14} className="text-purple-500" />
                  <span className="text-xs text-slate-500">Total</span>
                </div>
                <p className="text-xl font-bold text-slate-900">{data.total_users_to_date || 0}</p>
              </div>
            </div>
            
            {/* Sparkline */}
            {data.sparkline && data.sparkline.length > 0 && (
              <div className="flex items-center justify-between bg-white rounded-lg p-3 border">
                <span className="text-xs text-slate-500">7-Day Trend</span>
                <Sparkline data={data.sparkline} color="#6366f1" />
              </div>
            )}
            
            {/* WAU/MAU if available */}
            {(data.wau || data.mau) && (
              <div className="flex gap-4 text-xs text-slate-500">
                {data.wau && <span>WAU: <strong className="text-slate-700">{data.wau}</strong></span>}
                {data.mau && <span>MAU: <strong className="text-slate-700">{data.mau}</strong></span>}
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-6 text-slate-500">
            <ChartLine size={32} className="mx-auto mb-2 text-slate-300" />
            <p className="text-sm">No adoption data available</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdoptionWidget;
